/**
 * Utilidades para debugging y diagnóstico de errores
 */

import { supabase } from '../lib/supabase';

export interface DiagnosticResult {
  test: string;
  status: 'success' | 'error' | 'warning';
  message: string;
  details?: any;
}

/**
 * Ejecutar diagnósticos completos del sistema
 */
export const runSystemDiagnostics = async (): Promise<DiagnosticResult[]> => {
  const results: DiagnosticResult[] = [];

  // Test 1: Variables de entorno
  results.push(await testEnvironmentVariables());

  // Test 2: Conexión a Supabase
  results.push(await testSupabaseConnection());

  // Test 3: Autenticación
  results.push(await testAuthentication());

  // Test 4: Base de datos
  results.push(await testDatabase());

  // Test 5: Permisos RLS
  results.push(await testRLSPolicies());

  return results;
};

/**
 * Test de variables de entorno
 */
const testEnvironmentVariables = async (): Promise<DiagnosticResult> => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseKey) {
    return {
      test: 'Variables de Entorno',
      status: 'error',
      message: 'Variables de entorno de Supabase no configuradas',
      details: {
        VITE_SUPABASE_URL: supabaseUrl ? 'Configurado' : 'No configurado',
        VITE_SUPABASE_ANON_KEY: supabaseKey ? 'Configurado' : 'No configurado'
      }
    };
  }

  if (supabaseUrl.includes('placeholder') || supabaseKey.includes('placeholder')) {
    return {
      test: 'Variables de Entorno',
      status: 'error',
      message: 'Variables de entorno contienen valores placeholder',
      details: {
        url: supabaseUrl,
        key: supabaseKey.substring(0, 20) + '...'
      }
    };
  }

  if (!supabaseUrl.startsWith('https://') || !supabaseUrl.includes('.supabase.co')) {
    return {
      test: 'Variables de Entorno',
      status: 'error',
      message: 'URL de Supabase inválida',
      details: {
        url: supabaseUrl,
        expected: 'https://tu-proyecto.supabase.co'
      }
    };
  }

  return {
    test: 'Variables de Entorno',
    status: 'success',
    message: 'Variables de entorno configuradas correctamente',
    details: {
      url: supabaseUrl,
      key: supabaseKey.substring(0, 20) + '...'
    }
  };
};

/**
 * Test de conexión a Supabase
 */
const testSupabaseConnection = async (): Promise<DiagnosticResult> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('count')
      .limit(1);

    if (error) {
      return {
        test: 'Conexión a Supabase',
        status: 'error',
        message: 'Error de conexión a Supabase',
        details: {
          error: error.message,
          code: error.code,
          details: error.details
        }
      };
    }

    return {
      test: 'Conexión a Supabase',
      status: 'success',
      message: 'Conexión a Supabase exitosa'
    };

  } catch (error) {
    return {
      test: 'Conexión a Supabase',
      status: 'error',
      message: 'Error de red o configuración',
      details: {
        error: error instanceof Error ? error.message : 'Error desconocido',
        type: error instanceof TypeError ? 'Network Error' : 'Unknown Error'
      }
    };
  }
};

/**
 * Test de autenticación
 */
const testAuthentication = async (): Promise<DiagnosticResult> => {
  try {
    // Verificar configuración de auth
    const { data: session } = await supabase.auth.getSession();
    
    return {
      test: 'Sistema de Autenticación',
      status: 'success',
      message: 'Sistema de autenticación funcionando',
      details: {
        hasSession: !!session.session,
        user: session.session?.user?.email || 'No autenticado'
      }
    };

  } catch (error) {
    return {
      test: 'Sistema de Autenticación',
      status: 'error',
      message: 'Error en sistema de autenticación',
      details: {
        error: error instanceof Error ? error.message : 'Error desconocido'
      }
    };
  }
};

/**
 * Test de base de datos
 */
const testDatabase = async (): Promise<DiagnosticResult> => {
  try {
    // Verificar que las tablas principales existan
    const tables = ['profiles', 'posts', 'comments', 'likes', 'follows', 'messages'];
    const tableTests = [];

    for (const table of tables) {
      try {
        const { error } = await supabase
          .from(table)
          .select('count')
          .limit(1);

        tableTests.push({
          table,
          exists: !error,
          error: error?.message
        });
      } catch (err) {
        tableTests.push({
          table,
          exists: false,
          error: err instanceof Error ? err.message : 'Error desconocido'
        });
      }
    }

    const missingTables = tableTests.filter(t => !t.exists);
    
    if (missingTables.length > 0) {
      return {
        test: 'Base de Datos',
        status: 'error',
        message: `Faltan ${missingTables.length} tablas en la base de datos`,
        details: {
          missingTables: missingTables.map(t => t.table),
          allTables: tableTests
        }
      };
    }

    return {
      test: 'Base de Datos',
      status: 'success',
      message: 'Todas las tablas principales existen',
      details: {
        tables: tableTests.map(t => t.table)
      }
    };

  } catch (error) {
    return {
      test: 'Base de Datos',
      status: 'error',
      message: 'Error verificando base de datos',
      details: {
        error: error instanceof Error ? error.message : 'Error desconocido'
      }
    };
  }
};

/**
 * Test de políticas RLS
 */
const testRLSPolicies = async (): Promise<DiagnosticResult> => {
  try {
    // Intentar una operación que requiere RLS
    const { error } = await supabase
      .from('profiles')
      .select('id')
      .limit(1);

    if (error && error.message.includes('RLS')) {
      return {
        test: 'Políticas RLS',
        status: 'error',
        message: 'Error en políticas de seguridad RLS',
        details: {
          error: error.message
        }
      };
    }

    return {
      test: 'Políticas RLS',
      status: 'success',
      message: 'Políticas RLS funcionando correctamente'
    };

  } catch (error) {
    return {
      test: 'Políticas RLS',
      status: 'warning',
      message: 'No se pudo verificar RLS completamente',
      details: {
        error: error instanceof Error ? error.message : 'Error desconocido'
      }
    };
  }
};

/**
 * Test específico para registro de usuarios
 */
export const testUserRegistration = async (testEmail: string = `test-${Date.now()}@example.com`) => {
  const results: DiagnosticResult[] = [];

  try {
    console.log('🧪 Probando registro de usuario con:', testEmail);

    // Test 1: Verificar que el email no exista
    const { data: existingUser } = await supabase
      .from('profiles')
      .select('email')
      .eq('email', testEmail)
      .single();

    if (existingUser) {
      results.push({
        test: 'Email Único',
        status: 'warning',
        message: 'Email ya existe en la base de datos',
        details: { email: testEmail }
      });
      return results;
    }

    // Test 2: Intentar registro
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: testEmail,
      password: 'test123456',
      options: {
        data: {
          name: 'Test User',
          role: 'athlete',
          sport: 'Testing'
        }
      }
    });

    if (authError) {
      results.push({
        test: 'Registro de Usuario',
        status: 'error',
        message: 'Error en registro de autenticación',
        details: {
          error: authError.message,
          code: authError.status
        }
      });
      return results;
    }

    results.push({
      test: 'Registro de Usuario',
      status: 'success',
      message: 'Usuario registrado exitosamente',
      details: {
        userId: authData.user?.id,
        email: authData.user?.email
      }
    });

    // Test 3: Verificar creación de perfil
    if (authData.user) {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authData.user.id)
        .single();

      if (profileError) {
        results.push({
          test: 'Creación de Perfil',
          status: 'error',
          message: 'Error al verificar perfil creado',
          details: {
            error: profileError.message
          }
        });
      } else if (profile) {
        results.push({
          test: 'Creación de Perfil',
          status: 'success',
          message: 'Perfil creado correctamente',
          details: {
            profileId: profile.id,
            name: profile.name,
            role: profile.role
          }
        });
      }
    }

  } catch (error) {
    results.push({
      test: 'Registro de Usuario',
      status: 'error',
      message: 'Error inesperado durante el test',
      details: {
        error: error instanceof Error ? error.message : 'Error desconocido'
      }
    });
  }

  return results;
};

/**
 * Obtener información de configuración actual
 */
export const getConfigurationInfo = () => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  return {
    environment: {
      supabaseUrl: supabaseUrl || 'No configurado',
      hasAnonKey: !!supabaseKey,
      keyPreview: supabaseKey ? supabaseKey.substring(0, 20) + '...' : 'No configurado'
    },
    validation: {
      urlFormat: supabaseUrl ? supabaseUrl.startsWith('https://') : false,
      urlDomain: supabaseUrl ? supabaseUrl.includes('.supabase.co') : false,
      keyLength: supabaseKey ? supabaseKey.length > 50 : false
    },
    browser: {
      userAgent: navigator.userAgent,
      online: navigator.onLine,
      cookiesEnabled: navigator.cookieEnabled
    }
  };
};